<template>
  <div class="card card-body shadow-sm mt-0">
    <div class="form-header d-flex justify-content-between align-items-center">
      <h4 class="form-title">{{ route.params.id ? 'Editar Saída de Animal' : 'Nova Saída de Animal' }}</h4>
      <button class="btn btn-secondary btn-sm me-3" @click="voltar">Voltar</button>
    </div>
    <BaseForm 
      :isFormValid="isFormValid"
      :isSubmitting="isSubmitting"
      @submit="salvarSaidaAnimal"
      @cancel="voltar"
    >
    <FormSection title="Dados da Saída">
      <!-- Animal -->
      <div class="form-group mb-3">
        <label for="animais" class="form-label">
          Animal <span class="required">*</span>
        </label>
        <div class="d-flex">
          <input
            id="animais"
            type="text"
            class="form-control animal-input"
            :value="saidaAnimal.animal_nome || ''"
            disabled
            style="width: 250px;"
          />
          <button
            class="btn btn-primary pessoa-icon-btn"
            type="button"
            @click="abrirModal"
          >
            <i class="bi bi-person"></i>
          </button>
        </div>
        <span v-if="formSubmitted && errors.id_animal" class="error-message">
          {{ errors.id_animal }}
        </span>
      </div>

      <div class="form-group mb-3">
        <!-- Motivo -->
        <InputField
          v-model="saidaAnimal.motivo"
          label="Motivo <span class='required'>*</span>"
          id="motivo"
          required
          width="220px"
        />
      </div>

      <div class="form-group mb-3">
        <!-- Local -->
        <InputField
          v-model="saidaAnimal.local"
          label="Local <span class='required'>*</span>"
          id="local"
          required
          width="220px"
        />
      </div>

      <!-- Estado -->
      <SelectField
        v-model="saidaAnimal.id_estado"
        label="Estado <span class='required'>*</span>"
        id="estado"
        :options="estados.map(e => ({ value: e.id, text: `${e.nome} (${e.sigla})` }))"
        @change="carregarMunicipios"
        required
        :style="{ width: '200px' }"
      />

      <!-- Município -->
      <SelectField
        v-model="saidaAnimal.id_municipio"
        label="Município <span class='required'>*</span>"
        id="municipio"
        :options="municipios.map(m => ({ value: m.id, text: m.nome }))"
        required
        :style="{ width: '200px' }"
      />

      <!-- Responsável -->
      <InputField
        v-model="saidaAnimal.responsavel_tipo"
        label="Tipo de Responsável <span class='required'>*</span>"
        id="responsavel_tipo"
        required
      />
      <InputField
        v-model="saidaAnimal.responsavel_nome"
        label="Nome do Responsável <span class='required'>*</span>"
        id="responsavel"
        required
        width="200px"
      />

      <!-- Datas Previstas -->
      <InputField
        v-model="saidaAnimal.data_saida_prevista"
        label="Saída Prevista <span class='required'>*</span>"
        id="data_saida_prevista"
        type="date"
        required
      />
      <InputField
        v-model="saidaAnimal.data_retorno_prevista"
        label="Retorno Previsto <span class='required'>*</span>"
        id="data_retorno_prevista"
        type="date"
        required
      />

      <!-- Datas e Horas Efetivas -->
      <div class="d-flex gap-3 mt-2">
        <InputField
          v-model="saidaAnimal.data_saida_efetiva"
          label="Data Saída Efetiva"
          id="data_saida_efetiva"
          type="date"
        />
        <InputField
          v-model="saidaAnimal.hora_saida_efetiva"
          label="Hora Saída Efetiva"
          id="hora_saida_efetiva"
          type="time"
        />
      </div>
      <div class="d-flex gap-3 mt-2">
        <InputField
          v-model="saidaAnimal.data_retorno_efetiva"
          label="Data Retorno Efetiva"
          id="data_retorno_efetiva"
          type="date"
        />
        <InputField
          v-model="saidaAnimal.hora_retorno_efetiva"
          label="Hora Retorno Efetiva"
          id="hora_retorno_efetiva"
          type="time"
        />
      </div>

      <!-- Observações -->
      <InputField
        v-model="saidaAnimal.observacoes"
        label="Observações"
        id="observacoes"
        type="textarea"
        class="w-100"

      />
    </FormSection>


    <!-- Seção de arquivos -->
    <div class="arquivos-container">
      <h5 class="arquivos-title">Arquivos</h5>

      <!-- Botão de adicionar arquivos -->
      <div class="d-flex mb-2">
        <button 
          type="button" 
          class="btn btn-primary btn-sm me-2" 
          @click="selecionarArquivos" 
          :disabled="!isFormValid"
        >
          Adicionar
        </button>
        <input type="file" ref="fileInput" multiple @change="handleFileUpload" class="d-none" />
      </div>

      <!-- Exibir alerta apenas se os campos obrigatórios ainda não estiverem preenchidos -->
      <p v-if="!isFormValid" class="text-danger">
        ⚠️ Preencha os campos obrigatórios antes de anexar arquivos.
      </p>

      <!-- Tabela de arquivos -->
      <div class="table-responsive">
        <table class="table table-bordered">
          <thead class="table-light">
            <tr>
              <th>Tipo</th>
              <th>Nome</th>
              <th>Data</th>
              <th>Arquivo</th>
              <th>Tamanho</th>
              <th>Ações</th>
            </tr>
          </thead>
          <tbody>
            <tr v-if="arquivos.length === 0">
              <td colspan="6" class="text-center">Nenhum arquivo anexado.</td>
            </tr>
            <tr v-for="arquivo in arquivos" :key="arquivo.id_arquivo">
              <td>{{ arquivo.tipo || 'Não informado' }}</td>
              <td>{{ arquivo.nome }}</td>
              <td>{{ formatarData(arquivo.data_upload) }}</td>
              <td>
                <a
                  :href="fileUrl(arquivo.caminho)"
                  target="_blank"
                  rel="noopener"
                >
                  Download
                </a>
              </td>
              <td>{{ formatarTamanho(arquivo.tamanho) }}</td>
              <td>
                <button class="btn btn-danger btn-sm" type="button" @click="excluirArquivo(arquivo.id_arquivo)">
                  <i class="bi bi-trash"></i>
                </button>
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>
      
    </BaseForm>

    <!-- Modal de Seleção de Animal (Padronizado) -->
    <div v-if="showModal" class="modal-overlay">
      <div class="modal-container">
        <!-- Cabeçalho do Modal -->
        <div class="modal-header">
          <h6>Selecione um Animal</h6>
          <div class="header-actions">
            <i class="bi bi-x-circle close-icon" @click="fecharModal"></i>
          </div>
        </div>

        <!-- Barra de Pesquisa (Filtros) -->
        <div class="modal-filters">
          <label class="filter-label">Filtro:</label>
          <div class="filter-group">
            <input 
              type="text" 
              v-model="busca" 
              placeholder="Buscar por Nome" 
              class="filter-input"
              @input="filtrarPessoas"
            />
            <!-- Botão Limpar Filtros -->
            <b-button
                variant="secondary"
                class="small-filter-btn"
                @click="limparFiltros"
            >
              <i class="bi bi-funnel mr-2" title="Limpar Filtro"></i>
            </b-button>
          </div>
        </div>

        <!-- Tabela de Animais -->
        <div class="table-container">
          <table class="table">
              <thead>
                <tr>
                  <th>Nome</th>
                  <th>Proprietário</th>
                  <th>Ações</th>
                </tr>
              </thead>
              <tbody>
                <tr v-if="animaisFiltrados.length === 0">
                  <td colspan="3">Nenhum animal encontrado.</td>
                </tr>
                <tr v-for="animal in animaisFiltrados" :key="animal.id">
                  <td>{{ animal.nome }}</td>
                  <td>
                    {{ animal.proprietario_nome_pessoa_fisica || animal.proprietario_nome_pessoa_juridica || 'Não informado' }}
                  </td>
                  <td>
                    <button class="btn btn-success btn-sm" @click="selecionarAnimal(animal)">
                      <i class="bi bi-check-lg"></i>
                    </button>
                  </td>
                </tr>
              </tbody>
            </table>
        </div>

        <!-- Rodapé com Paginação -->
        <div class="modal-footer">
          <span class="total-registros">{{ totalRegistros }} registros</span>
          <div class="pagination-container">
            <button 
              class="btn btn-light pagination-btn" 
              :disabled="pagina <= 1" 
              @click="mudarPagina(pagina - 1)"
            >
              <i class="bi bi-chevron-left"></i>
            </button>
            <span class="pagination-text">Página {{ pagina }} de {{ totalPaginas }}</span>
            <button 
              class="btn btn-light pagination-btn" 
              :disabled="pagina >= totalPaginas" 
              @click="mudarPagina(pagina + 1)"
            >
              <i class="bi bi-chevron-right"></i>
            </button>
          </div>
        </div>

      </div>
    </div>
    <!-- FIM do Modal de Seleção de Animal -->
  </div>
</template>

<script>
import { reactive, ref, computed, onMounted } from "vue";
import { useRoute, useRouter } from "vue-router";
import axios from "@/services/axios";
import BaseForm from "@/components/Layout/BaseForm.vue";
import FormSection from "@/components/Layout/FormSection.vue";
import InputField from '@/components/Layout/InputField.vue';
import SelectField from '@/components/Layout/SelectField.vue';
import { BButton } from 'bootstrap-vue-next';
import globalUtils from "@/utils/globalUtils";


export default {
  components: { BaseForm, FormSection, InputField, SelectField, BButton },
  setup() {

    const route = useRoute();
    const router = useRouter();

    const showModal = ref(false);
    const busca = ref("");
    const formSubmitted = ref(false);
    const tempId = ref(-Math.floor(Math.random() * 1000000)); // Gera um ID temporário negativo

    const pagina = ref(1);
    const totalPaginas = ref(1);
    const totalRegistros = ref(0);

    const saidaAnimal = reactive({
      animal: "",
      motivo: "",
      local: "",
      id_estado: "",
      id_municipio: "",
      responsavel_tipo: "",
      responsavel_nome: "",
      data_saida_prevista: "",
      data_retorno_prevista: "",
      data_saida_efetiva: "",
      hora_saida_efetiva: "",
      data_retorno_efetiva: "",
      hora_retorno_efetiva: "",
      observacoes: "",
      id: route.params.id || null,
    });

    const arquivos = ref([]);
    const fileInput = ref(null);
    const isSubmitting = ref(false);

    const isFormValid = computed(() => {
      return (
        !!saidaAnimal.animal &&
        !!saidaAnimal.motivo &&
        !!saidaAnimal.local &&
        !!saidaAnimal.id_estado &&
        !!saidaAnimal.id_municipio &&
        !!saidaAnimal.responsavel_tipo &&
        !!saidaAnimal.responsavel_nome &&
        !!saidaAnimal.data_saida_prevista &&
        !!saidaAnimal.data_retorno_prevista

      );
    });

    const proprietarios = ref([]);
    const carregarProprietarios = async () => {
      try {
        const response = await axios.get('/cadastros/pessoas-fisicas');
        // Supondo que a resposta contenha um array em response.data.data
        proprietarios.value = response.data.data;
      } catch (error) {
        console.error("Erro ao carregar proprietários:", error);
      }
    };

    const getNomeProprietario = (animal) => {
      // animal.proprietario_id referencia o id da pessoa física
      const proprietario = proprietarios.value.find(p => Number(p.id) === Number(animal.proprietario_id));
      return proprietario ? proprietario.nome : "Não informado";
    };


    const selecionarArquivos = () => {
      fileInput.value.click(); // Simula clique no input de arquivos oculto
    };

    const carregarArquivos = async () => {

      if (!saidaAnimal.id && !tempId.value) {
        console.warn("🚨 Nenhum ID válido para carregar arquivos.");
        return;
      }

      try {
        const { data } = await axios.get(`/operacional/saidas-animais/arquivos/${saidaAnimal.id || tempId.value}`);
        // opcional: adiciona campo url pronto (não obrigatório para o template acima)
        arquivos.value = (data || []).map(a => ({
          ...a,
          url: fileUrl(a.caminho),
        }));
      } catch (error) {
        console.error("Erro ao carregar arquivos:", error);
      }
    };

    const limparFiltros = () => {
      busca.value = "";
      carregarAnimais();
    };

    const handleFileUpload = async (event) => {
      if (!isFormValid.value) {
        alert("Preencha os campos obrigatórios antes de anexar arquivos.");
        return;
      }

      const files = event.target.files;
      if (!files.length) return;

      const formData = new FormData();
      for (let file of files) {
        formData.append("arquivos", file);
      }

      const id = saidaAnimal.id || tempId.value;
      try {
        isSubmitting.value = true;
        await axios.post(`/operacional/saidas-animais/upload/${id}`, formData, {
          headers: { "Content-Type": "multipart/form-data" },
        });
        carregarArquivos();
        // limpa seleção
        event.target.value = '';
      } catch (error) {
        console.error("Erro ao enviar arquivos:", error);
      } finally {
        isSubmitting.value = false;
      }
    };

    const excluirArquivo = async (id_arquivo, event) => {
      if (!confirm("Tem certeza que deseja excluir este arquivo?")) return;

      try {
        if (event) event.preventDefault();
        await axios.delete(`/operacional/saidas-animais/arquivos/${id_arquivo}`);
        arquivos.value = arquivos.value.filter((a) => a.id_arquivo !== id_arquivo);
      } catch (error) {
        console.error("Erro ao excluir arquivo:", error);
      }
    };

    // URL absoluta e compatível com qualquer formato vindo da API
    const fileUrl = (caminho) => {
      if (!caminho) return '';
      const rel = globalUtils.normalizeUploadPath(caminho);
      return globalUtils.asAbsoluteUrl(rel);
    };

    const animaisFiltrados = computed(() => {
      return animais.value.filter(animal => animal.nome.toLowerCase().includes(busca.value.toLowerCase()));
    });

    const abrirModal = async () => {
        await carregarAnimais();
        showModal.value = true;
      };
  
    const fecharModal = () => {
      showModal.value = false;
    };

    const selecionarAnimal = (animal) => {
      saidaAnimal.animal = animal.id;
      saidaAnimal.animal_nome = animal.nome;
      fecharModal();
    };

    const estados = ref([]);
    const municipios = ref([]);
    const animais = ref([]);
    const errors = reactive({ backend: null });
    
    const carregarSaidaAnimal = async () => {
      if (!route.params.id) {
        // Resetar valores ao criar um novo registro
        Object.assign(saidaAnimal, {
          animal: "",
          motivo: "",
          local: "",
          id_estado: "",
          id_municipio: "",
          responsavel_tipo: "",
          responsavel_nome: "",
          data_saida_prevista: "",
          data_retorno_prevista: "",
          data_saida_efetiva: "",
          hora_saida_efetiva: "",
          data_retorno_efetiva: "",
          hora_retorno_efetiva: "",
          observacoes: "",
          id: null,
        });
        return;
      }

      try {
        const response = await axios.get(`/operacional/saidas-animais/${route.params.id}`);
        
        // Função para formatar datas corretamente
        const formatarData = (data) => data ? data.split("T")[0] : "";

        Object.assign(saidaAnimal, {
          animal: Number(response.data.id_animal),
          motivo: response.data.motivo,
          local: response.data.local,
          id_estado: Number(response.data.id_estado),
          id_municipio: Number(response.data.id_municipio),
          responsavel_tipo: response.data.responsavel_tipo,
          responsavel_nome: response.data.responsavel_nome,
          data_saida_prevista: formatarData(response.data.data_saida_prevista),
          data_retorno_prevista: formatarData(response.data.data_retorno_prevista),
          data_saida_efetiva: formatarData(response.data.data_saida_efetiva),
          hora_saida_efetiva: response.data.hora_saida_efetiva || "",
          data_retorno_efetiva: formatarData(response.data.data_retorno_efetiva),
          hora_retorno_efetiva: response.data.hora_retorno_efetiva || "",
          observacoes: response.data.observacoes || "",
        });
      } catch (error) {
        console.error("Erro ao carregar saída de animal:", error);
      }
    };

    const salvarSaidaAnimal = async () => {
        if (isSubmitting.value) return;
        formSubmitted.value = true;
        isSubmitting.value = true;

        try {
            const method = saidaAnimal.id ? "put" : "post";
            const url = saidaAnimal.id 
              ? `/operacional/saidas-animais/${saidaAnimal.id}` 
              : `/operacional/saidas-animais`;

            const payload = {
                id_animal: Number(saidaAnimal.animal),
                motivo: saidaAnimal.motivo,
                local: saidaAnimal.local,
                id_estado: Number(saidaAnimal.id_estado),
                id_municipio: Number(saidaAnimal.id_municipio),
                responsavel_tipo: saidaAnimal.responsavel_tipo,
                responsavel_nome: saidaAnimal.responsavel_nome,
                data_saida_prevista: saidaAnimal.data_saida_prevista || null,
                data_retorno_prevista: saidaAnimal.data_retorno_prevista || null,
                data_saida_efetiva: saidaAnimal.data_saida_efetiva || null,
                hora_saida_efetiva: saidaAnimal.hora_saida_efetiva || null,
                data_retorno_efetiva: saidaAnimal.data_retorno_efetiva || null,
                hora_retorno_efetiva: saidaAnimal.hora_retorno_efetiva || null,
                observacoes: saidaAnimal.observacoes || null,
            };

            const response = await axios[method](url, payload);
            const novoId = response.data.id;

            // 🔄 🛠️ Se estivermos criando uma nova saída, migrar arquivos temporários para o ID real
            if (!saidaAnimal.id) {
                try {
                    await axios.put(`/operacional/saidas-animais/arquivos/migrar/${tempId.value}`, { novo_id: novoId });
                    tempId.value = novoId; // Atualizar tempId com o ID real
                } catch (error) {
                    console.error("❌ Erro ao migrar arquivos:", error);
                }
            }

            router.push(`/operacional/saidas-animais`);

        } catch (error) {
            console.error("❌ Erro ao salvar saída de animal:", error);
            errors.backend = error.response?.data?.message || "Ocorreu um erro ao salvar a saída de animal.";
        } finally {
            isSubmitting.value = false;
        }
    };

    const carregarAnimais = async () => {
      try {
        // Supondo que sua API suporte paginação com um parâmetro "page"
        const response = await axios.get(`/cadastros/animais`, { params: { page: pagina.value, busca: busca.value } });
        animais.value = response.data.data || [];
        totalPaginas.value = response.data.totalPages || 1;
        totalRegistros.value = response.data.totalRecords || 0;
      } catch (error) {
        console.error("Erro ao carregar animais:", error);
      }
    };

    const mudarPagina = (novaPagina) => {
      if (novaPagina > 0 && novaPagina <= totalPaginas.value) {
        pagina.value = novaPagina;
        carregarAnimais();
      }
    };

    const filtrarPessoas = () => {
      pagina.value = 1;
      carregarAnimais();
    };


    const carregarEstados = async () => {
      try {
        const response = await axios.get(`/localidades/estados`);
        estados.value = response.data;
      } catch (error) {
        console.error('Erro ao carregar estados:', error);
      }
    };

    const carregarMunicipios = async () => {
      try {
        if (!saidaAnimal.id_estado) {
          municipios.value = [];
          return;
        }
        const response = await axios.get(`/localidades/municipios/${saidaAnimal.id_estado}`);
        municipios.value = response.data;
      } catch (error) {
        console.error('Erro ao carregar municípios:', error);
      }
    };

    const voltar = async () => {
        if (tempId.value < 0) {
            try {
                await axios.delete(`/operacional/saidas-animais/arquivos/limpar/${tempId.value}`);
            } catch (error) {
                console.error("❌ Erro ao excluir arquivos temporários:", error);
            }
        }
        router.push("/operacional/saidas-animais");
    };

    const formatarData = (data) => {
      if (!data) return "Não informado";
      const date = new Date(data);
      return date.toLocaleDateString("pt-BR");
    };

    const formatarTamanho = (tamanho) => {
      if (!tamanho) return "0 KB";
      return (tamanho / 1024).toFixed(2) + " KB";
    };


    onMounted(async () => {
      carregarArquivos();
      await carregarProprietarios();
      await carregarAnimais();
      carregarSaidaAnimal();
      await carregarEstados();
      const saidaId = route.params.id;

      if (saidaId) {
        try {
          const response = await axios.get(`/operacional/saidas-animais/${saidaId}`);
          Object.assign(saidaAnimal, response.data);

          if (saidaAnimal.data_saida_prevista) {
            const date = new Date(saidaAnimal.data_saida_prevista);
            saidaAnimal.data_saida_prevista = isNaN(date.getTime()) ? '' : date.toISOString().split('T')[0];
          }

          if (saidaAnimal.data_saida_efetiva) {
            const date = new Date(saidaAnimal.data_saida_efetiva);
            saidaAnimal.data_saida_efetiva = isNaN(date.getTime()) ? '' : date.toISOString().split('T')[0];
          }

          if (saidaAnimal.data_retorno_prevista) {
            const date = new Date(saidaAnimal.data_retorno_prevista);
            saidaAnimal.data_retorno_prevista = isNaN(date.getTime()) ? '' : date.toISOString().split('T')[0];
          }

          if (saidaAnimal.data_retorno_efetiva) {
            const date = new Date(saidaAnimal.data_retorno_efetiva);
            saidaAnimal.data_retorno_efetiva = isNaN(date.getTime()) ? '' : date.toISOString().split('T')[0];
          }

          if (saidaAnimal.id_estado) {
            await carregarMunicipios();
          }

          if (saidaAnimal.id) {
            carregarArquivos();
          }

        } catch (error) {
          console.error('Erro ao carregar pessoa física:', error);
        }
      } else {
        carregarSaidaAnimal();
      }
    });

    return {
      getNomeProprietario,
      saidaAnimal,
      isFormValid,
      formSubmitted,
      salvarSaidaAnimal,
      voltar,
      animais,
      carregarEstados,
      carregarMunicipios,
      estados,
      municipios,
      isSubmitting,
      errors,
      route,
      showModal,
      abrirModal,
      fecharModal,
      selecionarAnimal,
      busca,
      animaisFiltrados,
      arquivos,
      handleFileUpload,
      fileUrl,
      selecionarArquivos,
      excluirArquivo,
      formatarData,
      formatarTamanho,
      fileInput,
      limparFiltros,
      mudarPagina,
      filtrarPessoas,
      pagina,
      totalPaginas,
      totalRegistros,
      carregarAnimais,
    };
  },
};
</script>

<style scoped>

.card {
  max-width: 100%;
  margin: 0 auto;
}

.arquivos-container {
  margin-top: 0.1rem;
  padding: 1rem;
  background-color: #f9f9f9;
  border-radius: 4px;
  border: 1px solid #ccc;
}

.arquivos-container,
.arquivos-container * {
  font-family: inherit;
  font-size: 0.73rem;
}

.modal-header {
    background: #ccc; /* cor cinza */
    padding: 10px 15px;
    display: flex;
    justify-content: space-between;
    align-items: center;
  }


  /* Estilos gerais do modal */
  .modal-overlay {
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: rgba(0, 0, 0, 0.5);
    z-index: 9999;
    display: flex;
    align-items: center;
    justify-content: center;
  }

  .modal-container {
  background: #fff;
  width: 100%;
  max-width: 700px; /* ajuste conforme necessário */
  height: 600px; /* define a altura fixa */
  overflow-y: auto; /* adiciona barra de rolagem vertical se necessário */
  margin: 0 10px;
  border-radius: 4px;
  /* Remova margens duplicadas se estiverem interferindo */
}

    /* Conteúdo do modal */
    .modal-content {
    padding: 15px;
  }


.modal {
  display: block;
  background: white;
  width: 60%;
  max-width: 90%;
  max-height: 80%;
  overflow-y: auto;
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
}

  /* Ícone de fechar */
  .close-icon {
    font-size: 1.5rem;
    cursor: pointer;
  }


/* Barra de pesquisa */
.modal-filters {
  display: flex;
  align-items: center;
  gap: 10px;
}

.filter-label {
  font-size: 12px;
  font-weight: bold;
  color: #333;
  margin-top: 10px;
  margin-left: 10px;
}

.filter-group {
  display: flex;
  flex-grow: 1;
  gap: 10px;
  margin-right: 40px;
}

.filter-input {
  flex: 1;
  padding: 8px 10px;
  border: 1px solid #ccc;
  border-radius: 0px;
  font-size: 10px;
  outline: none;
  transition: 0.2s;
  margin-left: 8px;
  margin-right: 15px;
}

.filter-input:focus {
  border-color: #007bff;
  box-shadow: 0 0 5px rgba(0, 123, 255, 0.3);
}

/* Tabela */
.table-container {
  margin-top: 15px;
  margin-left: 15px;
  margin-right: 15px;
}

/* Ajusta a altura das células e centraliza verticalmente os conteúdos */
.table th, .table td {
  padding: 8px 10px; /* Reduz um pouco o espaçamento */
  line-height: 1.2; /* Ajusta a altura da linha */
  vertical-align: middle; /* Centraliza verticalmente */
}

.table td .btn-sm {
  padding: 4px 6px; /* Reduz o padding */
  font-size: 0.85rem; /* Diminui um pouco o tamanho do texto */
  height: 15px; /* Ajusta a altura */
  width: 15px; /* Mantém o botão quadrado */
  display: flex;
  align-items: center;
  justify-content: center;
}

.table th {
  background: #f1f1f1;
  font-weight: bold;
}

.table td {
  padding: 8px;
  border-bottom: 1px solid #ddd;
}

/* Rodapé */
/* 🔹 Ajusta a paginação e centraliza no Modal */
.modal-footer {
  display: flex;
  justify-content: center; /* Centraliza a paginação */
  align-items: center;
  margin-top: 10px;
  padding-top: 10px;
}

/* 🔹 Ajusta o layout da paginação */
.pagination-container {
  display: flex;
  align-items: center;
  gap: 10px; /* Espaço entre os elementos */
}

/* 🔹 Estiliza os botões de navegação da paginação */
.pagination-btn {
  padding: 6px 10px;
  font-size: 14px;
  border-radius: 6px;
  display: flex;
  align-items: center;
  justify-content: center;
}

/* 🔹 Estiliza o texto de página */
.pagination-text {
  font-size: 12px;
  font-weight: normal;
  color: #333;
}

/* 🔹 Ajusta a área de registros */
.total-registros {
  position: absolute;
  left: 20px;
  font-size: 14px;
  color: #555;
}

/* Ajusta a barra de filtros */
.modal-filters {
  display: flex;
  align-items: center;
  gap: 10px;
  margin-top: 15px;
}

/* Ajusta o grupo de filtros */
.filter-group {
  display: flex;
  flex-grow: 1;
  gap: 10px;
  align-items: center;
}

/* Estiliza o botão de limpar filtros */
.filter-btn {
  background: #6c757d;
  border: none;
  color: white;
  padding: 6px 12px;
  font-size: 16px;
  border-radius: 6px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
}

.filter-btn i {
  font-size: 18px;
}

.filter-btn:hover {
  background: #1a1b1b;
}

.small-filter-btn {
  padding: 7px 10px;      /* Reduz o espaçamento interno */
  font-size: 12px;       /* Fonte menor para o botão */
  line-height: 1;
}

.small-filter-btn i {
  font-size: 10px;       /* Diminui o tamanho do ícone, se desejar */
}

</style>
